import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Grid,
    TextField,
    Button,
    Typography,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import api from '../../../../config/axiosConfig';
import { renderField } from '../formUtils';
import { toEnglishDigits, toPersianDigits } from '../../../../utils/convertNumbers';
import { createValidatedHandleChange, validateFormLanguage, DEFAULT_FIELD_MAPPINGS } from '../../../../utils/inputValidation';

// Constants
const PROFILE_ONLY_FIELDS = new Set(['role', 'image']);

// Helper: return a clean path (works with absolute/relative), ensures trailing slash
const normalizeBasePath = (urlLike) => {
    if (!urlLike) return null;
    try {
        // absolute
        const u = new URL(urlLike);
        return u.pathname.endsWith('/') ? u.pathname : `${u.pathname}/`;
    } catch {
        // relative
        const path = urlLike.startsWith('http')
            ? urlLike // some weird absolute that new URL failed on
            : urlLike; // relative like '/accounts/api/v1/profile/students/'
        const withSlash = path.endsWith('/') ? path : `${path}/`;
        // If a full http URL was passed, strip origin part so axios baseURL is used
        if (withSlash.startsWith('http')) {
            try {
                return new URL(withSlash).pathname.replace(/\/+$/, '/') // single trailing slash
            } catch {
                return withSlash; // last resort
            }
        }
        return withSlash;
    }
};

// Remove undefined/null/empty-string values; keep 0/false
const pruneEmpty = (obj) => {
    const out = {};
    Object.entries(obj || {}).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        if (typeof v === 'string' && v.trim() === '') return;
        out[k] = v;
    });
    return out;
};

function UserForm({ user, editFields, fetchUrl, submitUrl, onSuccess, onCancel }) {
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [generalError, setGeneralError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    // 409 duplicate_student from register/: matches to show before creating anyway.
    const [duplicateMatches, setDuplicateMatches] = useState(null);
    const isEditMode = !!user;

    // Initialize form data
    useEffect(() => {
        if (user) {
            // Edit mode - populate with existing user data
            setFormData(user);
        } else {
            // Create mode - initialize empty form
            const initialData = {};
            editFields.forEach(field => {
                if (field.name === 'role') {
                    // role is optional, don't set default
                    return;
                }
                initialData[field.name] = '';
            });
            setFormData(initialData);
        }
    }, [user, editFields]);

    // Build UI object with Persian digits for display
    const uiObject = useMemo(() => {
        if (!formData) return {};
        const skipLatin = new Set(
            editFields
                .filter(f => f.keepLatinDigits || f.type === 'date' || f.inputMode === 'latin')
                .map(f => f.name)
        );
        const out = { ...formData };
        for (const [k, v] of Object.entries(formData)) {
            if (skipLatin.has(k)) continue;
            if (typeof v === 'string' && /\d/.test(v)) {
                // Convert to Persian for display (will be converted back on submit)
                out[k] = v; // Keep as is, renderField will handle Persian conversion
            }
        }
        return out;
    }, [formData, editFields]);

    // Field mappings for validation (can be customized per form)
    const fieldMappings = {
        farsiOnly: ['first_name', 'last_name', 'address'],
        numeric: ['national_id', 'mobile'],
        englishOnly: ['email', 'password', 'password2'],
    };

    // Base handleChange function (handles digit conversion)
    const baseHandleChange = (fieldName, value) => {
        const field = editFields.find(f => f.name === fieldName);
        const shouldKeepLatin = field?.keepLatinDigits || field?.type === 'date' || field?.inputMode === 'latin';
        
        // For numeric fields (mobile, national_id), keep Persian digits in formData for display
        // They will be converted to English only when submitting
        const isNumericField = fieldMappings.numeric.includes(fieldName);

        // Convert Persian/Arabic-Indic digits to English for storage
        // BUT: Don't convert numeric fields during typing - keep Persian digits for display
        const normalized = typeof value === 'string' && !shouldKeepLatin && !isNumericField
            ? toEnglishDigits(value)
            : value;

        setFormData(prev => ({ ...prev, [fieldName]: normalized }));
    };

    // Create validated handleChange that blocks invalid input
    const handleChange = createValidatedHandleChange(baseHandleChange, setErrors, fieldMappings);

    const validateForm = () => {
        const newErrors = {};

        // Validate language constraints
        const languageValidation = validateFormLanguage(formData, editFields, fieldMappings);
        Object.assign(newErrors, languageValidation.errors);

        // Validate required fields and patterns
        editFields.forEach(field => {
            if (field.condition && !field.condition(formData)) {
                return; // Skip validation for conditional fields that aren't shown
            }

            const value = formData[field.name];
            
            if (field.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
                newErrors[field.name] = `${field.label} الزامی است`;
            }

            // Pattern validation
            // For numeric fields, convert to English digits and remove formatting before pattern check
            if (field.pattern && value) {
                let valueToTest = value;
                
                // For numeric fields, convert Persian/Arabic-Indic digits to English and remove dots/spaces
                if (fieldMappings.numeric.includes(field.name) && typeof value === 'string') {
                    valueToTest = toEnglishDigits(value).replace(/[.\s-]/g, ''); // Remove dots, spaces, dashes
                }
                
                const regex = new RegExp(field.pattern);
                if (!regex.test(valueToTest)) {
                    newErrors[field.name] = field.errorMessage || `${field.label} معتبر نیست`;
                }
            }
        });

        // Password confirmation validation (only in create mode)
        if (!isEditMode && formData.password && formData.password2) {
            if (formData.password !== formData.password2) {
                newErrors.password2 = 'رمزهای عبور مطابقت ندارند';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e, { allowDuplicate = false } = {}) => {
        if (e && e.preventDefault) e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setGeneralError('');

        try {
            if (isEditMode) {
                // Edit mode: PATCH user and profile separately
                const userPayload = {};
                const profilePayloadRaw = {};

                editFields.forEach(field => {
                    const name = field.name;
                    if (!(name in formData)) return;
                    let value = formData[name];
                    
                    // Convert Persian digits to English for numeric fields before sending to API
                    if (fieldMappings.numeric.includes(name) && typeof value === 'string') {
                        value = toEnglishDigits(value);
                    }
                    
                    if (PROFILE_ONLY_FIELDS.has(name)) {
                        profilePayloadRaw[name] = value;
                    } else {
                        userPayload[name] = value;
                    }
                });

                const profilePayload = pruneEmpty(profilePayloadRaw);

                // 1) PATCH user fields
                if (Object.keys(userPayload).length > 0 && user.user_id) {
                    await api.patch(`/accounts/api/v1/users/${user.user_id}/`, userPayload);
                }

                // 2) PATCH profile-only fields
                if (Object.keys(profilePayload).length > 0 && user.id) {
                    const base = normalizeBasePath(submitUrl || fetchUrl);
                    if (base) {
                        await api.patch(`${base}${user.id}/`, profilePayload);
                    }
                }
            } else {
                // Create mode: Use registration endpoint
                // Note: This requires profile_type to be determined by the parent component
                // For now, we'll need to add profile_type to the form or get it from context
                // Since we're in UserList, the profile_type is implicit (student/teacher/staff)
                // We'll need to pass it as a prop or determine it from the fetchUrl
                
                // Determine profile_type from fetchUrl
                let profileType = 'student'; // default
                if (fetchUrl.includes('/teachers/')) {
                    profileType = 'teacher';
                } else if (fetchUrl.includes('/staffs/')) {
                    profileType = 'staff';
                }

                // Convert Persian digits to English for numeric fields before sending to API
                const registrationPayload = {
                    first_name: formData.first_name || '',
                    last_name: formData.last_name || '',
                    email: formData.email || '',
                    password: formData.password || '',
                    password2: formData.password2 || '',
                    national_id: formData.national_id ? toEnglishDigits(formData.national_id) : '',
                    mobile: formData.mobile ? toEnglishDigits(formData.mobile) : '',
                    address: formData.address || '',
                    birth_date: formData.birth_date || null,
                    profile_type: profileType,
                };
                if (allowDuplicate) registrationPayload.allow_duplicate = true;

                // Add role for staff
                if (profileType === 'staff' && formData.role) {
                    registrationPayload.staff_data = { role: formData.role };
                }

                await api.post('/accounts/api/v1/register/', registrationPayload);
            }

            onSuccess();
        } catch (error) {
            console.error('Error saving user:', error);
            const errorData = error.response?.data;

            if (error.response?.status === 409 && errorData?.error_code === 'duplicate_student') {
                setDuplicateMatches(errorData.matches || []);
                return;
            }

            // Set field-specific errors
            const newErrors = {};
            if (errorData && typeof errorData === 'object') {
                Object.keys(errorData).forEach(key => {
                    if (Array.isArray(errorData[key])) {
                        newErrors[key] = errorData[key][0];
                    } else if (typeof errorData[key] === 'string') {
                        newErrors[key] = errorData[key];
                    } else if (typeof errorData[key] === 'object') {
                        // Handle nested errors
                        Object.keys(errorData[key]).forEach(nestedKey => {
                            newErrors[`${key}.${nestedKey}`] = errorData[key][nestedKey];
                        });
                    }
                });
            }
            setErrors(newErrors);

            // Non-field errors ("error"/"detail" keys, HTML 500 pages, network
            // failures) have no field to attach to — show them above the buttons
            // instead of failing silently.
            const fieldNames = new Set(fieldsToShow.map(f => f.name));
            const hasVisibleFieldError = Object.keys(newErrors).some(k => fieldNames.has(k));
            const backendMessage =
                (errorData && typeof errorData === 'object' &&
                    (newErrors.error || newErrors.detail || newErrors.non_field_errors)) || '';
            if (backendMessage) {
                setGeneralError(backendMessage);
            } else if (!hasVisibleFieldError) {
                setGeneralError('ثبت انجام نشد. لطفاً دوباره تلاش کنید و در صورت تکرار خطا به پشتیبانی اطلاع دهید.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const reactivateExisting = async (match) => {
        setIsSubmitting(true);
        setGeneralError('');
        try {
            await api.patch(`/accounts/api/v1/users/${match.id}/`, { is_active: true });
            setDuplicateMatches(null);
            onSuccess();
        } catch (err) {
            const d = err.response?.data;
            setGeneralError(
                (d && (d.detail || d.error || (Array.isArray(d.is_active) ? d.is_active[0] : null))) ||
                'فعال‌سازی انجام نشد.'
            );
            setDuplicateMatches(null);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Filter fields for create vs edit mode and add password fields for create
    const fieldsToShow = useMemo(() => {
        if (isEditMode) {
            // In edit mode, don't show password fields
            return editFields.filter(f => f.name !== 'password' && f.name !== 'password2');
        } else {
            // In create mode, add password fields
            const passwordFields = [
                { name: 'password', label: 'پسورد', type: 'password', required: true, keepLatinDigits: true },
                { name: 'password2', label: 'تکرار پسورد', type: 'password', required: true, keepLatinDigits: true },
            ];
            // editOnly fields (e.g. is_active) don't apply when creating.
            return [...editFields.filter(f => !f.editOnly), ...passwordFields];
        }
    }, [isEditMode, editFields]);

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={3}>
                {fieldsToShow.map((field) => {
                    const fieldComponent = renderField(
                        field,
                        field.name,
                        uiObject,
                        handleChange,
                        errors
                    );

                    return (
                        <Grid item xs={12} sm={6} key={field.name}>
                            {fieldComponent}
                        </Grid>
                    );
                })}
            </Grid>

            {generalError && (
                <Alert severity="error" sx={{ mt: 3 }}>
                    {generalError}
                </Alert>
            )}

            <Dialog open={Array.isArray(duplicateMatches)} onClose={() => setDuplicateMatches(null)} dir="rtl">
                <DialogTitle>هنرجویی با همین مشخصات قبلاً ثبت شده است</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        اگر همان شخص است، از رکورد قبلی استفاده کنید (هنرجوی غیرفعال را می‌توانید همین‌جا فعال کنید).
                        فقط اگر واقعاً فرد دیگری است، «ثبت به‌عنوان هنرجوی جدید» را بزنید.
                    </Typography>
                    <List dense>
                        {(duplicateMatches || []).map((m) => (
                            <ListItem
                                key={m.id}
                                secondaryAction={
                                    !m.is_active ? (
                                        <Button size="small" variant="outlined" disabled={isSubmitting} onClick={() => reactivateExisting(m)}>
                                            فعال‌سازی
                                        </Button>
                                    ) : null
                                }
                            >
                                <ListItemText
                                    primary={`${m.first_name} ${m.last_name}${m.is_active ? '' : ' (غیرفعال)'}`}
                                    secondary={m.mobile ? toPersianDigits(m.mobile) : ''}
                                />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDuplicateMatches(null)} disabled={isSubmitting}>انصراف</Button>
                    <Button
                        color="warning"
                        disabled={isSubmitting}
                        onClick={() => { setDuplicateMatches(null); handleSubmit(null, { allowDuplicate: true }); }}
                    >
                        ثبت به‌عنوان هنرجوی جدید
                    </Button>
                </DialogActions>
            </Dialog>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                >
                    {isSubmitting ? 'در حال ذخیره...' : (isEditMode ? 'ویرایش' : 'ثبت')}
                </Button>
            </Box>
        </Box>
    );
}

export default UserForm;

